import { useState } from 'react';
import { cn } from '@/lib/cn';

/**
 * Package Qualifier — the pre-sales lead-capture microapp at /qualifier.
 *
 * React port of `docs/orbis-qualifier 2.html`. The source widget was written
 * for a Webflow Embed with its own dark-navy + steel-blue + amber palette;
 * this version keeps the questions, logic, and result-computation rules
 * verbatim but re-skins it in the landing brand palette so it reads as part
 * of orbiswisdomx.com — light background, `primary-500` orange for CTAs and
 * the result tier, Inter throughout, and font sizes that match the rest of
 * the landing pages rather than the widget's tighter defaults.
 *
 * Submission:
 *  · POSTs the answers + contact fields as JSON to `VITE_LEAD_ENDPOINT`.
 *  · The endpoint is a Google Apps Script Web App bound to a Google Sheet
 *    (see `docs/apps script lead capture 2.js` + `docs/vignesh instructions.md`
 *    for the ~15 min setup — a Google-account task, not a code change).
 *  · If the env var is unset or the request fails, the widget still shows
 *    the tier recommendation and logs a warning — the UX never hangs on a
 *    broken backend during dev or a demo.
 *  · Point `VITE_LEAD_ENDPOINT` at a real CRM webhook (HubSpot, Salesforce,
 *    etc.) later without touching this file.
 */
const LEAD_ENDPOINT = (import.meta.env.VITE_LEAD_ENDPOINT as string | undefined) ?? '';

interface Option {
  label: string;
  value: string;
}
interface Question {
  key: keyof Answers;
  text: string;
  logLabel: string;
  options: Option[];
}
interface Answers {
  estate?: string;
  baseline?: string;
  entities?: string;
  risk?: string;
  regulatory?: string;
  buyer?: string;
}

const QUESTIONS: Question[] = [
  {
    key: 'estate',
    text: 'Roughly how many business applications or systems would be in scope?',
    logLabel: 'Estate size',
    options: [
      { label: 'Fewer than 250', value: 'lt250' },
      { label: '250 – 1,000', value: '250to1000' },
      { label: '1,000 – 5,000', value: '1000to5000' },
      { label: 'More than 5,000', value: 'gt5000' },
    ],
  },
  {
    key: 'baseline',
    text: 'Do you have a reliable baseline of your current application estate (CMDB, ServiceNow, LeanIX, etc.)?',
    logLabel: 'Baseline data',
    options: [
      { label: 'Yes, well maintained', value: 'clear' },
      { label: 'Partial / scattered across tools', value: 'partial' },
      { label: 'No reliable baseline exists', value: 'none' },
    ],
  },
  {
    key: 'entities',
    text: 'How many business units, legal entities or geographies are involved?',
    logLabel: 'Complexity',
    options: [
      { label: 'One business unit', value: 'single' },
      { label: 'A few, within one entity', value: 'few' },
      { label: 'Multiple legal entities / geographies', value: 'multi' },
    ],
  },
  {
    key: 'risk',
    text: 'Is threat intelligence, vulnerability correlation or blast-radius risk a priority for you?',
    logLabel: 'Risk priority',
    options: [
      { label: 'Yes, a major priority', value: 'yes' },
      { label: 'Somewhat relevant', value: 'some' },
      { label: 'Not a focus right now', value: 'no' },
    ],
  },
  {
    key: 'regulatory',
    text: 'Do you operate under a non-standard or bespoke regulatory reporting requirement?',
    logLabel: 'Regulatory need',
    options: [
      { label: 'Yes, bespoke requirements', value: 'yes' },
      { label: 'Standard regulatory reporting only', value: 'no' },
    ],
  },
  {
    key: 'buyer',
    text: 'Who would ultimately sign off a system-closure decision at your organisation?',
    logLabel: 'Sign-off level',
    options: [
      { label: 'CIO / CTO / Board-level', value: 'exec' },
      { label: 'Enterprise architecture / IT leadership', value: 'ea' },
      { label: 'Not yet established', value: 'unclear' },
    ],
  },
];

const TOTAL_STEPS = QUESTIONS.length + 1;

interface LogEntry {
  ts: string;
  label: string;
  value: string;
}

interface Result {
  tier: string;
  price: string;
  why: string;
  leadWithAssessment: boolean;
}

/** Tier + why-text derivation — copied verbatim from the source widget. */
function computeResult(answers: Required<Answers>): Result {
  let tier: string;
  let price: string;
  switch (answers.estate) {
    case 'lt250':
      tier = 'Orbis Foundation';
      price = '£30,000 / year';
      break;
    case '250to1000':
      tier = 'Orbis Professional';
      price = '£60,000 / year';
      break;
    case '1000to5000':
      tier = 'Orbis Enterprise';
      price = '£120,000 / year';
      break;
    case 'gt5000':
    default:
      tier = 'Orbis Strategic';
      price = 'Private offer (£180k–£300k+)';
      break;
  }

  const why: string[] = [];
  let leadWithAssessment = false;
  if (answers.baseline === 'none' || answers.baseline === 'partial') {
    leadWithAssessment = true;
    why.push(
      "Your estate baseline isn't fully established yet — we'd typically start with a 6-week paid System Closure Assessment (£15k–£25k) to build the evidence base before sizing a subscription.",
    );
  }
  if (
    answers.entities === 'multi' &&
    (answers.estate === '1000to5000' ||
      answers.estate === 'lt250' ||
      answers.estate === '250to1000')
  ) {
    why.push(
      'Multiple legal entities or geographies typically push scope up a tier from application count alone.',
    );
    if (tier === 'Orbis Foundation') {
      tier = 'Orbis Professional';
      price = '£60,000 / year';
    } else if (tier === 'Orbis Professional') {
      tier = 'Orbis Enterprise';
      price = '£120,000 / year';
    }
  }
  const modules: string[] = [];
  if (answers.risk === 'yes') modules.push('Metis Risk Intelligence');
  if (answers.regulatory === 'yes') modules.push('a Bespoke Regulatory / Data Pack');
  if (modules.length) {
    why.push(
      `Based on your priorities, you'd likely also want ${modules.join(' and ')} layered on top.`,
    );
  }
  if (tier === 'Orbis Strategic') {
    why.push(
      "At this scale, pricing is scoped and negotiated directly rather than published — we'll bring a specialist into the conversation.",
    );
  }
  if (answers.buyer === 'unclear') {
    why.push("We'll help you build the internal case as part of the first conversation.");
  }

  return { tier, price, why: why.join(' '), leadWithAssessment };
}

function timestamp(): string {
  return new Date().toTimeString().slice(0, 8);
}

export function PackageQualifier() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [log, setLog] = useState<LogEntry[]>([]);
  const [contact, setContact] = useState({ name: '', email: '', company: '' });
  const [contactError, setContactError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<Result | null>(null);

  function pickAnswer(q: Question, opt: Option) {
    setAnswers((prev) => ({ ...prev, [q.key]: opt.value }));
    setLog((prev) => [...prev, { ts: timestamp(), label: q.logLabel, value: opt.label }]);
    setStep((s) => s + 1);
  }

  async function submitContact() {
    const name = contact.name.trim();
    const email = contact.email.trim();
    const company = contact.company.trim();
    const emailOk = /\S+@\S+\.\S+/.test(email);
    if (!name || !emailOk) {
      setContactError(true);
      return;
    }
    setContactError(false);

    const computed = computeResult(answers as Required<Answers>);
    const payload = {
      name,
      email,
      company,
      answers,
      recommendedTier: computed.tier,
      submittedAt: new Date().toISOString(),
    };

    setSubmitting(true);
    if (LEAD_ENDPOINT) {
      try {
        await fetch(LEAD_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } catch (e) {
        if (import.meta.env.DEV) console.warn('[qualifier] lead submission failed', e);
      }
    } else if (import.meta.env.DEV) {
      console.info('[qualifier] VITE_LEAD_ENDPOINT unset — skipping POST; payload was:', payload);
    }
    setSubmitting(false);
    setLog((prev) => [...prev, { ts: timestamp(), label: 'Contact captured', value: name }]);
    setResult(computed);
  }

  const showingContact = step === QUESTIONS.length && !result;
  const currentStepIndex = result ? TOTAL_STEPS : step;

  return (
    <main className="w-full bg-gradient-to-b from-landing-peach to-white px-5 pb-20 pt-10 sm:px-6 md:px-8 md:pt-14 lg:px-10 lg:pb-24 lg:pt-16 xl:px-16 2xl:px-[133px]">
      <div className="mx-auto w-full max-w-[760px]">
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.14em] text-primary-500 lg:text-sm">
          Orbis · Package Qualifier
        </p>
        <h1 className="mb-4 text-[30px] font-bold leading-tight tracking-[-0.01em] text-landing-heading sm:text-[36px] lg:text-[44px] 2xl:text-[48px]">
          Which Orbis package fits your estate?
        </h1>
        <p className="mb-10 max-w-[58ch] text-base leading-[1.6] text-landing-body sm:text-lg lg:text-xl">
          Answer six questions about your application estate and governance needs. We&apos;ll show
          an indicative package and put you in touch with the right person — no commitment.
        </p>

        {/* Audit log of answered questions */}
        {log.length > 0 && (
          <div className="mb-6 flex flex-col gap-2.5">
            {log.map((entry, i) => (
              <div
                key={i}
                className="animate-in fade-in slide-in-from-top-1 flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-lg border border-l-[3px] border-emerald-200 border-l-emerald-500 bg-emerald-50 px-4 py-3 text-[15px] duration-300"
              >
                <span className="shrink-0 font-mono text-[13px] font-medium text-emerald-700">
                  {entry.ts}
                </span>
                <span className="text-landing-body">
                  <b className="font-semibold text-landing-heading">{entry.label}:</b>{' '}
                  <span className="text-landing-body/85">{entry.value}</span>
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_10px_40px_-20px_rgba(15,23,42,0.15)] sm:p-8 lg:p-10">
          {/* Progress dots */}
          <div className="mb-8 flex gap-1.5">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <span
                key={i}
                className={cn(
                  'h-1 flex-1 rounded-full transition-colors',
                  i < currentStepIndex && 'bg-emerald-500',
                  i === currentStepIndex && 'bg-primary-500',
                  i > currentStepIndex && 'bg-slate-200',
                )}
              />
            ))}
          </div>

          {!result && step < QUESTIONS.length && (
            <QuestionStep
              stepIndex={step}
              total={QUESTIONS.length}
              question={QUESTIONS[step]!}
              onPick={pickAnswer}
            />
          )}

          {!result && showingContact && (
            <ContactStep
              contact={contact}
              onChange={setContact}
              error={contactError}
              submitting={submitting}
              onSubmit={submitContact}
            />
          )}

          {result && <ResultStep result={result} />}
        </div>

        <footer className="mt-6 text-center text-xs text-landing-muted lg:text-sm">
          Estimates are indicative and confirmed during a scoping call · WisdomX Orbis
        </footer>
      </div>
    </main>
  );
}

function QuestionStep({
  stepIndex,
  total,
  question,
  onPick,
}: {
  stepIndex: number;
  total: number;
  question: Question;
  onPick: (q: Question, opt: Option) => void;
}) {
  return (
    <>
      <div className="mb-3 text-xs font-semibold uppercase tracking-[0.1em] text-primary-500 lg:text-sm">
        Question {stepIndex + 1} of {total}
      </div>
      <p className="mb-6 text-[19px] font-semibold leading-[1.4] text-landing-heading lg:text-[22px]">
        {question.text}
      </p>
      <div className="flex flex-col gap-3">
        {question.options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onPick(question, opt)}
            className={cn(
              'group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 text-left text-base font-medium text-landing-body',
              'transition-colors hover:border-primary-500 hover:bg-primary-50 hover:text-landing-heading',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 lg:text-[17px]',
            )}
          >
            <span>{opt.label}</span>
            <span
              aria-hidden="true"
              className="text-primary-500 opacity-0 transition-opacity group-hover:opacity-100"
            >
              →
            </span>
          </button>
        ))}
      </div>
    </>
  );
}

function ContactStep({
  contact,
  onChange,
  error,
  submitting,
  onSubmit,
}: {
  contact: { name: string; email: string; company: string };
  onChange: (c: { name: string; email: string; company: string }) => void;
  error: boolean;
  submitting: boolean;
  onSubmit: () => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="mb-3 text-xs font-semibold uppercase tracking-[0.1em] text-primary-500 lg:text-sm">
        Last step
      </div>
      <p className="mb-6 text-[19px] font-semibold leading-[1.4] text-landing-heading lg:text-[22px]">
        Where should we send your indicative package and next steps?
      </p>

      <Field id="fname" label="Name">
        <input
          id="fname"
          type="text"
          value={contact.name}
          onChange={(e) => onChange({ ...contact, name: e.target.value })}
          placeholder="Jane Smith"
          className={inputClass}
        />
      </Field>
      <Field id="femail" label="Work email">
        <input
          id="femail"
          type="email"
          value={contact.email}
          onChange={(e) => onChange({ ...contact, email: e.target.value })}
          placeholder="jane@company.com"
          className={inputClass}
        />
      </Field>
      <Field id="fcompany" label="Company">
        <input
          id="fcompany"
          type="text"
          value={contact.company}
          onChange={(e) => onChange({ ...contact, company: e.target.value })}
          placeholder="Company name"
          className={inputClass}
        />
      </Field>

      {error && (
        <p className="-mt-1 mb-3 text-sm font-medium text-red-600">
          Please enter your name and a work email.
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className={cn(
          'mt-3 w-full rounded-lg bg-primary-500 px-8 py-4 text-lg font-semibold text-white transition-colors',
          'hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700',
          'disabled:cursor-not-allowed disabled:opacity-60',
        )}
      >
        {submitting ? 'Sending…' : 'See my indicative package'}
      </button>
    </form>
  );
}

function ResultStep({ result }: { result: Result }) {
  return (
    <>
      <div className="mb-4 inline-flex items-center rounded-full bg-primary-500 px-3 py-1 text-xs font-bold uppercase tracking-[0.08em] text-white lg:text-sm">
        {result.leadWithAssessment ? 'Entry offer recommended' : 'Indicative package'}
      </div>
      <div className="mb-1 text-[34px] font-bold leading-tight text-primary-500 lg:text-[40px]">
        {result.tier}
      </div>
      <div className="mb-6 font-mono text-lg text-landing-heading lg:text-xl">{result.price}</div>
      <p className="mb-6 border-b border-slate-200 pb-6 text-base leading-[1.65] text-landing-body lg:text-lg">
        {result.why ||
          'This reflects the scope you described — a member of our pre-sales team will confirm it on a short call.'}
      </p>
      <div className="pt-4 text-center">
        <h2 className="mb-3 text-[24px] font-bold text-landing-heading lg:text-[28px]">
          Thanks — you&apos;re on our radar
        </h2>
        <p className="mx-auto max-w-[50ch] text-base leading-[1.65] text-landing-body lg:text-lg">
          A member of the Orbis pre-sales team will follow up shortly to walk through this in more
          detail and answer any questions about your estate.
        </p>
      </div>
    </>
  );
}

const inputClass = cn(
  'mb-4 w-full rounded-lg border border-slate-200 bg-white px-4 py-3.5 text-base text-landing-heading',
  'placeholder:text-landing-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 lg:text-lg',
);

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <>
      <label htmlFor={id} className="mb-2 block text-sm font-medium text-landing-body lg:text-base">
        {label}
      </label>
      {children}
    </>
  );
}
